/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import Ajv, { Schema, ValidateFunction } from "ajv";

import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TGenericConfigValue = unknown;

export type TConfigData = { [K: string]: TGenericConfigValue };

/**
 * Service to manage configuration
 */
export class EditorConfigService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** AJV instance */
	private ajv = new Ajv({
		allErrors: true
	});

	/** Registered config schemas */
	private schemas: {
		[K: string]: {
			validate: ValidateFunction;
			default: TGenericConfigValue;
		};
	} = {};

	/** Config data */
	private data: TConfigData = {};

	/** Config changed event */
	public onConfigChanged = createEventEmitter<{ key: string }>();

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.logger = dc.get("logger").facility("ConfigService");
	}

	/**
	 * Registers config key
	 *
	 * @param key Key
	 * @param schema Validation schema
	 * @param defaultValue Default value
	 */
	public register<T>(key: string, schema: Schema, defaultValue: T): void {
		if (this.schemas[key]) {
			throw new Error(`Config schema '${key}' is already registered.`);
		}

		const validateFn = this.ajv.compile(schema);

		if (!validateFn(defaultValue)) {
			throw new Error(`Failed to validate default value for config key '${key}'.`);
		}

		this.schemas[key] = {
			validate: validateFn,
			default: defaultValue
		};

		this.data[key] = defaultValue;
	}

	/**
	 * Returns the config value by key
	 *
	 * @param key Key
	 */
	public get<T>(key: string): T {
		if (!this.schemas[key]) {
			throw new Error(`Config key '${key}' is not registered.`);
		}

		return this.data[key] as T;
	}

	/**
	 * Stores the config under given key
	 *
	 * @param key Key
	 * @param value Value
	 */
	public set<T>(key: string, value: T): void {
		if (!this.schemas[key]) {
			throw new Error(`Config key '${key}' is not registered.`);
		}

		const validateFn = this.schemas[key].validate;

		if (!validateFn(value)) {
			this.logger.error(
				`Failed to set configuration for key '${key}': Invalid value`,
				validateFn.errors
			);
			throw new Error(`Failed to validate config value.`);
		}

		this.data[key] = value;

		emitEvent(this.onConfigChanged, { key: key });
	}

	/**
	 * Returns if the key is registered
	 *
	 * @param key Key
	 */
	public has(key: string): boolean {
		return this.schemas[key] ? true : false;
	}

	/**
	 * Returns exported configuration data
	 */
	public export(): TConfigData {
		return this.data;
	}

	/**
	 * Imports configuration data and returns if it was sucessfull.
	 * Validation rrrors are logged via logger.
	 *
	 * @param data Data
	 */
	public import(data: TConfigData): boolean {
		let hasErrors = false;

		for (const k in this.schemas) {
			if (data[k] === undefined) {
				this.data[k] = this.schemas[k].default;
				continue;
			}

			const validateFn = this.schemas[k].validate;

			if (validateFn(data[k])) {
				this.data[k] = data[k];
			} else {
				this.data[k] = this.schemas[k].default;

				this.logger.error(
					`Failed to import configuration for key '${k}': Invalid value`,
					validateFn.errors
				);
				hasErrors = true;
			}
		}

		emitEvent(this.onConfigChanged, { key: null });

		return !hasErrors;
	}
}
